import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Invocation() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚙️ JavaScript Function Invocation</h1>

      <p className="tutorial-intro">
        <strong>Function invocation</strong> means executing or calling a
        function. When a function is invoked, the code inside it runs.
        JavaScript functions can be invoked in several ways — directly, as
        methods, using <code className="highlight_code">call()</code>,
        <code className="highlight_code">apply()</code>, or
        <code className="highlight_code">bind()</code>.
      </p>
      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Functions run only when they are invoked.</li>
          <li>
            Invocation determines what{" "}
            <code className="highlight_code">this</code> refers to.
          </li>
          <li>
            <code className="highlight_code">call()</code> and{" "}
            <code className="highlight_code">apply()</code> invoke immediately,
            while <code className="highlight_code">bind()</code> returns a new
            bound function.
          </li>
          <li>IIFE executes immediately without an explicit call.</li>
        </ul>
      </div>

      {/* 1️⃣ Direct Invocation */}
      <div className="tutorial-section">
        <h3>1️⃣ Direct Function Invocation</h3>
        <p>
          The simplest way — calling a function by its name followed by
          parentheses.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet() {
  console.log("Hello from function!");
}

greet(); // Function is invoked`}
          />
          <TryItButton
            code={`function greet(){console.log("Hello from function!");}greet();`}
          />
        </div>
        <OutputBox>Output: Hello from function!</OutputBox>
      </div>

      {/* 2️⃣ Function as Method */}
      <div className="tutorial-section">
        <h3>2️⃣ Invoking a Function as a Method</h3>
        <p>
          When a function is part of an object, it’s called a{" "}
          <strong>method</strong>. The value of{" "}
          <code className="highlight_code">this</code> refers to the object.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Alice",
  greet: function() {
    console.log("Hello, " + this.name);
  }
};

person.greet(); // Method invocation`}
          />
          <TryItButton
            code={`const person={name:"Alice",greet:function(){console.log("Hello, "+this.name);}};person.greet();`}
          />
        </div>
        <OutputBox>Output: Hello, Alice</OutputBox>
      </div>

      {/* 3️⃣ call() and apply() */}
      <div className="tutorial-section">
        <h3>
          3️⃣ Invoking with <code className="highlight_code">call()</code> and{" "}
          <code className="highlight_code">apply()</code>
        </h3>
        <p>
          You can manually set the value of{" "}
          <code className="highlight_code">this</code> using{" "}
          <code className="highlight_code">call()</code> or{" "}
          <code className="highlight_code">apply()</code>.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function introduce() {
  console.log("Hi, I'm " + this.name);
}

const user = { name: "Bob" };
introduce.call(user); // Using call()`}
          />
          <TryItButton
            code={`function introduce(){console.log("Hi, I'm "+this.name);}const user={name:"Bob"};introduce.call(user);`}
          />
        </div>
        <OutputBox>Output: Hi, I'm Bob</OutputBox>
      </div>

      {/* 4️⃣ Using bind() */}
      <div className="tutorial-section">
        <h3>
          4️⃣ Invoking with <code className="highlight_code">bind()</code>
        </h3>
        <p>
          <code className="highlight_code">bind()</code> returns a new function
          with a permanently bound <code className="highlight_code">this</code>{" "}
          value.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function sayHello() {
  console.log("Hello, " + this.name);
}

const person = { name: "Charlie" };
const boundHello = sayHello.bind(person);

boundHello(); // Using bind()`}
          />
          <TryItButton
            code={`function sayHello(){console.log("Hello, "+this.name);}const person={name:"Charlie"};const boundHello=sayHello.bind(person);boundHello();`}
          />
        </div>
        <OutputBox>Output: Hello, Charlie</OutputBox>
      </div>

      {/* 5️⃣ Self-Invoking Function */}
      <div className="tutorial-section">
        <h3>5️⃣ Self-Invoking Function (IIFE)</h3>
        <p>
          A function can be invoked immediately after it is defined — known as
          an <strong>Immediately Invoked Function Expression</strong> (IIFE).
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`(function() {
  console.log("IIFE executed automatically!");
})();`}
          />
          <TryItButton
            code={`(function(){console.log("IIFE executed automatically!");})();`}
          />
        </div>
        <OutputBox>Output: IIFE executed automatically!</OutputBox>
      </div>

      {/* Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Explore <strong>Function Parameters</strong> to learn how arguments
          are passed and handled during invocation.
        </p>
      </div>
    </div>
  );
}
